import React from 'react';

const formatCurrency = (value) => {
  const amount = Number(value || 0);

  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(amount);
};

const icons = {
  spend: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="w-6 h-6"
    >
      <rect x="3" y="5" width="18" height="14" rx="3" />
      <path d="M16 12h.01" />
      <path d="M3 9h18" />
    </svg>
  ),

  orders: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="w-6 h-6"
    >
      <path d="M6 3h12l2 4H4l2-4Z" />
      <path d="M5 7v13h14V7" />
      <path d="M9 11h6" />
    </svg>
  ),

  average: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="w-6 h-6"
    >
      <path d="M4 19V9" />
      <path d="M10 19V5" />
      <path d="M16 19v-7" />
      <path d="M22 19H2" />
    </svg>
  ),

  favorite: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="w-6 h-6"
    >
      <path d="M12 21s-7-4.5-7-11a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 6.5-7 11-7 11Z" />
    </svg>
  ),
};

function LoadingValue() {
  return (
    <div className="mt-3 space-y-2 animate-pulse">
      <div className="h-7 bg-gray-200 rounded-lg w-3/4" />
      <div className="h-3 bg-gray-100 rounded-lg w-1/2" />
    </div>
  );
}

function CustomerInsightCards({ report, loading = false }) {
  const summary = report?.summary || {};
  const favoriteProducts = report?.favorite_products || [];

  const topProduct = favoriteProducts[0] || null;

  const cards = [
    {
      key: 'spend',
      label: 'Total Spend',
      value: formatCurrency(summary.total_spend),
      note: 'Across all completed orders',
      icon: icons.spend,
    },
    {
      key: 'orders',
      label: 'Total Orders',
      value: Number(summary.order_count || 0).toLocaleString(),
      note: 'Your completed purchase activity',
      icon: icons.orders,
    },
    {
      key: 'average',
      label: 'Average Order',
      value: formatCurrency(summary.average_order_value),
      note: 'Your average spend per order',
      icon: icons.average,
    },
    {
      key: 'favorite',
      label: 'Most Purchased',
      value: topProduct?.product_name || 'No data yet',
      note: topProduct
        ? `${Number(topProduct.quantity || 0).toLocaleString()} bought • ${formatCurrency(
            topProduct.amount
          )} spent`
        : 'Start shopping to unlock insights',
      icon: icons.favorite,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
      {cards.map((card) => (
        <div
          key={card.key}
          className="
            group
            relative
            bg-white
            rounded-3xl
            border border-gray-100
            shadow-lg
            overflow-hidden
            transition-all duration-300
            hover:-translate-y-1
            hover:shadow-2xl
            hover:border-primary/20
          "
        >
          <div className="absolute inset-x-0 top-0 h-1 bg-primary" />

          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-montserrat font-medium text-secondary/70">
                  {card.label}
                </p>
              </div>

              <div
                className="
                  w-11 h-11
                  rounded-2xl
                  bg-primary/10
                  text-primary
                  flex items-center justify-center
                  shrink-0
                  transition
                  group-hover:bg-primary
                  group-hover:text-white
                "
              >
                {card.icon}
              </div>
            </div>

            {loading ? (
              <LoadingValue />
            ) : (
              <>
                <h3
                  className={`
                    mt-5
                    font-lora
                    font-semibold
                    text-burgundy
                    leading-tight
                    break-words
                    ${
                      card.key === 'favorite'
                        ? 'text-xl'
                        : 'text-2xl sm:text-3xl'
                    }
                  `}
                >
                  {card.value}
                </h3>

                <p className="text-xs font-montserrat text-secondary/65 mt-3 leading-relaxed">
                  {card.note}
                </p>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default CustomerInsightCards;